import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import cloneDeep from "lodash/cloneDeep";
import setPath from "lodash/set";
import { ArrowLeft, Save, Send, Power, ExternalLink, QrCode, Smartphone, PenLine, Loader2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/admin/common";
import { IdentityPanel, ContactsPanel, SocialsPanel } from "@/components/editor/ContentPanels";
import { LinksPanel } from "@/components/editor/LinksPanel";
import { BlocksPanel } from "@/components/editor/BlocksPanel";
import { StylePanel } from "@/components/editor/StylePanel";
import { SettingsPanel } from "@/components/editor/SettingsPanel";
import { CardRenderer } from "@/components/card/CardRenderer";
import { api, formatErr, publicCardUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SECTIONS = [
  ["identity", "A · Identità", IdentityPanel],
  ["contacts", "B · Contatti", ContactsPanel],
  ["socials", "C · Social", SocialsPanel],
  ["links", "D · Link e CTA", LinksPanel],
  ["blocks", "E · Sezioni ed elementi", BlocksPanel],
];

const PhonePreview = ({ card, brand }) => {
  const preview = { ...card, links: card.links.filter((l) => l.visible), brand_name: brand };
  const info = () => toast.info("Il pulsante funziona sulla pagina pubblicata");
  return (
    <div className="phone-frame mx-auto" data-testid="live-preview-container">
      <div className="phone-screen" style={{ background: card.style.colors.background }}>
        <CardRenderer card={preview} onSave={info} onShare={info} />
      </div>
    </div>
  );
};

export default function Editor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [card, setCard] = useState(null);
  const [meta, setMeta] = useState(null);
  const [brand, setBrand] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mobileView, setMobileView] = useState("edit");
  const { user } = useAuth();
  const isClient = user?.role === "client";

  useEffect(() => {
    api.get(`/cards/${id}`).then((r) => setCard(r.data)).catch((e) => { toast.error(formatErr(e)); nav(isClient ? "/portale" : "/admin/card"); });
    if (!isClient) api.get("/meta").then((r) => setMeta(r.data));
    api.get("/public/settings").then((r) => setBrand(r.data.brand_name));
  }, [id, nav, isClient]);

  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const update = useCallback((path, value) => {
    setCard((prev) => setPath(cloneDeep(prev), path, value));
    setDirty(true);
  }, []);

  const save = async (silent) => {
    setBusy(true);
    try {
      const { name, identity, contacts, socials, links, blocks, style, settings, icons } = card;
      const { data } = await api.put(`/cards/${id}`, { name, identity, contacts, socials, links, blocks, style, settings, icons: icons || {} });
      setCard(data);
      setDirty(false);
      if (!silent) toast.success("Modifiche salvate");
      return true;
    } catch (e) {
      toast.error(formatErr(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status) => {
    if (dirty && !(await save(true))) return;
    try {
      await api.post(`/cards/${id}/status`, { status });
      setCard((c) => ({ ...c, status }));
      toast.success(status === "published" ? "Card pubblicata online" : "Card disattivata");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  if (!card) return <div className="grid min-h-screen place-items-center eyebrow">Caricamento editor…</div>;

  return (
    <div className="flex h-screen flex-col bg-[#F5F5F7]">
      <header className="flex flex-wrap items-center gap-3 border-b border-black/[0.06] bg-white/80 px-4 py-3 backdrop-blur-xl sm:px-6">
        <Link to={isClient ? "/portale" : card.client ? `/admin/clienti/${card.client.id}` : "/admin/card"} className="rounded-md p-2 hover:bg-zinc-100" data-testid="editor-back-button"><ArrowLeft className="h-4 w-4" /></Link>
        <div className="min-w-0 flex-1">
          <div className="eyebrow truncate">{card.client?.name}</div>
          <div className="flex items-center gap-2"><span className="truncate font-display text-xl" data-testid="editor-card-name">{card.name}</span><StatusBadge status={card.status} testId="editor-status-badge" /></div>
        </div>
        <span className={`hidden text-xs sm:block ${dirty ? "text-terra" : "text-zinc-400"}`} data-testid="editor-dirty-indicator">{dirty ? "Modifiche non salvate" : "Tutto salvato"}</span>
        <div className="flex flex-wrap gap-1.5">
          <Button variant="ghost" size="sm" onClick={() => window.open(publicCardUrl(card.slug), "_blank")} data-testid="editor-open-public"><ExternalLink className="h-4 w-4" /></Button>
          {!isClient && <Button variant="ghost" size="sm" onClick={() => nav(`/admin/card/${id}/qr`)} data-testid="nav-qr-link"><QrCode className="h-4 w-4" /></Button>}
          <Button variant="outline" size="sm" onClick={() => save()} disabled={busy || !dirty} data-testid="editor-save-button">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}{card.status === "published" ? "Salva" : "Salva bozza"}</Button>
          {isClient ? null : card.status === "published" ? (
            <Button size="sm" variant="outline" onClick={() => setStatus("disabled")} data-testid="editor-disable-button"><Power className="mr-1.5 h-4 w-4" />Disattiva</Button>
          ) : (
            <Button size="sm" className="bg-terra hover:opacity-90" onClick={() => setStatus("published")} data-testid="editor-publish-button"><Send className="mr-1.5 h-4 w-4" />Pubblica</Button>
          )}
        </div>
      </header>
      <div className="flex border-b bg-white lg:hidden">
        {[["edit", "Modifica", PenLine], ["preview", "Anteprima", Smartphone]].map(([k, l, I]) => (
          <button key={k} onClick={() => setMobileView(k)} data-testid={`editor-mobile-tab-${k}`} className={`flex flex-1 items-center justify-center gap-2 py-2.5 text-sm ${mobileView === k ? "border-b-2 border-zinc-900 font-medium" : "text-zinc-500"}`}><I className="h-4 w-4" />{l}</button>
        ))}
      </div>
      <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(420px,500px)_1fr]">
        <div className={`min-h-0 overflow-y-auto border-r border-black/[0.06] bg-[#FBFBFD] ${mobileView === "edit" ? "block" : "hidden"} lg:block`}>
          <Tabs defaultValue="content" className="p-4 sm:p-6">
            <TabsList className={`mb-5 grid w-full ${isClient ? "hidden" : "grid-cols-3"}`}>
              <TabsTrigger value="content" data-testid="editor-content-tab">Contenuti</TabsTrigger>
              <TabsTrigger value="style" data-testid="editor-style-tab-trigger">Stile</TabsTrigger>
              <TabsTrigger value="settings" data-testid="editor-settings-tab-trigger">Impostazioni</TabsTrigger>
            </TabsList>
            <TabsContent value="content">
              <Accordion type="multiple" defaultValue={["identity"]} className="space-y-2">
                {SECTIONS.map(([k, label, Panel]) => (
                  <AccordionItem key={k} value={k} className="rounded-2xl border border-black/[0.06] bg-white px-4">
                    <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline" data-testid={`accordion-${k}`}>{label}</AccordionTrigger>
                    <AccordionContent className="pb-4"><Panel card={card} update={update} /></AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </TabsContent>
            <TabsContent value="style"><StylePanel card={card} update={update} meta={meta} /></TabsContent>
            <TabsContent value="settings"><SettingsPanel card={card} update={update} onSlugChanged={(slug) => setCard((c) => ({ ...c, slug }))} /></TabsContent>
          </Tabs>
        </div>
        <div className={`min-h-0 overflow-y-auto bg-[#EDEDF0] py-8 ${mobileView === "preview" ? "block" : "hidden"} lg:block`}>
          <div className="mb-4 text-center eyebrow">Anteprima live · /c/{card.slug}</div>
          <PhonePreview card={card} brand={brand} />
        </div>
      </div>
    </div>
  );
}
